/**
 * MandantPage — Administration › Mandant & Branding (Story UI-U6 AC8).
 *
 * One page with underline tabs (design.md §5 — content sections of one entity):
 *   Mandant        — name/slug read-only + own_domains chip editor (GET/PATCH /admin/tenant)
 *   Portal         — full GET/PUT /admin/customer-portal config (enabled, branding,
 *                    6 feature toggles, invite criteria, reminder cascade, retention)
 *   Portal-Zugänge — per-subject invite email / trigger invite / list + revoke grants
 *   AVB            — the V10-S global library: list/search (GET /avb) + register (POST /avb)
 *
 * Each tab renders ONLY when the caller holds the tab's key(s) (server truth via
 * useEffectivePermissions). Per-policy AVB LINKING stays on the Police detail
 * (explicitly out of scope — no dead buttons here). Defensive: is_default surfaces
 * as "Standardwerte — noch nicht gespeichert"; the logo renders as an IMAGE preview
 * with an upload/replace file picker (POST /admin/customer-portal/branding/logo →
 * logoRef → setBrokerBranding) + a "Logo entfernen" button — never the raw ref string.
 */

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Plus, Upload } from 'lucide-react';
import {
  PORTAL_FEATURE_KEYS,
  CUSTOMER_STATUS_WIRE_VALUES,
  readBrokerBrandingColors,
  type PortalConfigDto,
  type PortalFeatureToggles,
  type BrokerBrandingDto,
} from '@brokly/shared';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../../components/ui/Tabs';
import { BrandingColorsEditor } from '../../../admin/features/branding/BrandingColorsEditor';
import type { BrandingColorsPatch } from '../../../admin/features/branding/BrandingColorsEditor';
import { apiClient, ApiError, API_BASE_URL } from '../../../lib/api-client';
import { cn } from '../../../lib/cn';
import { qk } from '../../../lib/query-keys';
import { AdminPageHeader } from '../AdminPageHeader';
import { ADMIN_PAGE_META } from '../adminNav';
import { PortalBerechtigungenPanel } from '../../portal-berechtigungen/PortalBerechtigungenPanel';
import {
  useTenantConfig,
  usePortalAdminConfig,
  useAvbLibrary,
  useMailVisibilityTeams,
} from '../hooks';
import { useEffectivePermissions } from '../useEffectivePermissions';
import {
  Button,
  LoadingState,
  ErrorState,
  EmptyState,
  SettingRow,
  Toggle,
  TextInput,
} from '../components';

const meta = ADMIN_PAGE_META.mandant;

const FEATURE_LABELS: Record<string, string> = {
  policen: 'Policen',
  rechnungen: 'Rechnungen',
  schadenmeldung: 'Schadenmeldung',
  feedback: 'Feedback',
  umfragen: 'Umfragen',
  beratung: 'Beratung',
};
/** MV: a configured default team the LOADED team list does not carry (deleted after it was set). */
const UNKNOWN_MAIL_TEAM_LABEL = 'Unbekanntes Team';
/**
 * MV: a configured default team whose name cannot be looked up, because the team list is out
 * of reach: no `admin.team.read`, or the read failed. Distinct from UNKNOWN_MAIL_TEAM_LABEL:
 * the team is correctly configured, only its name is missing.
 */
const UNRESOLVED_MAIL_TEAM_LABEL = 'Team gesetzt (Name nicht verfügbar)';
/** MV: the list is on its way. A name still arriving is not a name the viewer may not read. */
const LOADING_MAIL_TEAM_LABEL = 'Name wird geladen …';
/** Shown when the write failed for a reason the api did not put into words (e.g. no network). */
const MAIL_TEAM_WRITE_ERROR = 'Das Standard-Team konnte nicht gespeichert werden.';

const STATUS_LABELS: Record<string, string> = {
  leer: 'Leer',
  neu: 'Neu',
  provisorisch: 'Provisorisch',
  aktiv: 'Aktiv',
  gekuendigt: 'Gekündigt',
  inaktiv: 'Inaktiv',
};

// ── Mandant tab ─────────────────────────────────────────────────────────────
function MandantTab({
  canManage,
  canManageVisibility,
}: {
  canManage: boolean;
  canManageVisibility: boolean;
}) {
  const qc = useQueryClient();
  const { data, isLoading, isError } = useTenantConfig();
  const [domains, setDomains] = useState<string[] | null>(null);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  // MV: the fallback team for mail we could not match to a client. Its own write and its own
  // right (visibility.manage), so it saves on pick, not on the Domains "Speichern" button.
  const [mailTeamError, setMailTeamError] = useState<string | null>(null);
  // Held until the tenant read moves OFF the value it replaced, because the write persists
  // nothing in mock mode and needs a refetch in live mode, so the value would visibly snap
  // back. `base` is that replaced value: a third value written by someone else matches
  // neither, so it drops the pick and wins instead of hiding behind it forever.
  const [pickedMailTeam, setPickedMailTeam] = useState<{
    value: string | null;
    base: string | null;
  } | null>(null);
  const kundenteams = useMailVisibilityTeams();
  const serverMailTeam = data?.mail_default_team_id ?? null;
  // The api resolves the configured team's name inside the tenant, so a viewer with
  // tenant.read but WITHOUT admin.team.read still reads a name instead of a placeholder.
  // null here means either no team or a team row that is gone; the labels below cover both.
  const serverMailTeamName = data?.mail_default_team_name ?? null;
  useEffect(() => {
    if (pickedMailTeam !== null && serverMailTeam !== pickedMailTeam.base) {
      setPickedMailTeam(null);
    }
  }, [serverMailTeam, pickedMailTeam]);
  const saveMailDefaultTeam = useMutation({
    mutationFn: (teamId: string | null) => apiClient.admin.setMailDefaultTeam(teamId),
    onSuccess: () => {
      setMailTeamError(null);
      void qc.invalidateQueries({ queryKey: qk.adminTenant() });
    },
    // A failed write reverts the pick and says so: never a silent success.
    onError: (e: Error) => {
      setPickedMailTeam(null);
      // This route's own refusal is German and says WHY (team gone). Anything else is a
      // transport failure carrying technical English, or an api error with no words at all.
      setMailTeamError(e instanceof ApiError && e.message !== '' ? e.message : MAIL_TEAM_WRITE_ERROR);
    },
  });
  const effectiveMailTeam = pickedMailTeam !== null ? pickedMailTeam.value : serverMailTeam;
  // undefined = no list to look a name up in (still loading, or not readable), which is NOT
  // the same as a team missing from a list we did load.
  const mailTeams = kundenteams.data?.teams;
  const namedMailTeam = mailTeams?.find((t) => t.id === effectiveMailTeam);

  const effectiveDomains = domains ?? data?.own_domains ?? [];
  const save = useMutation({
    mutationFn: () => apiClient.admin.setTenantDomains(effectiveDomains),
    onSuccess: () => {
      setError(null);
      setDomains(null);
      void qc.invalidateQueries({ queryKey: qk.adminTenant() });
    },
    onError: (e: Error) => setError(e.message),
  });

  if (isLoading) return <LoadingState />;
  if (isError || data == null) return <ErrorState />;

  const addDomain = () => {
    const d = draft.trim().toLowerCase();
    if (d && !effectiveDomains.includes(d)) setDomains([...effectiveDomains, d]);
    setDraft('');
  };
  const removeDomain = (d: string) => setDomains(effectiveDomains.filter((x) => x !== d));
  const dirty = domains != null;

  return (
    <div className="space-y-5">
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">Name</label>
          <TextInput value={data.name} readOnly disabled />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">Slug</label>
          <TextInput value={data.slug} readOnly disabled className="font-mono" />
        </div>
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-muted-foreground">
          Eigene Domains
        </label>
        <p className="mb-2 text-xs text-muted-foreground">
          E-Mail-Domains dieses Mandanten (für die Zuordnung eingehender Nachrichten).
        </p>
        <div className="mb-2 flex flex-wrap gap-1.5">
          {effectiveDomains.length === 0 ? (
            <span className="text-sm text-muted-foreground">Keine Domains hinterlegt.</span>
          ) : (
            effectiveDomains.map((d) => (
              <span
                key={d}
                className="inline-flex items-center gap-1 rounded-md border border-border bg-muted px-2 py-0.5 text-xs"
              >
                <span className="font-mono">{d}</span>
                {canManage && (
                  <button
                    aria-label={`${d} entfernen`}
                    onClick={() => removeDomain(d)}
                    className="text-muted-foreground hover:text-status-danger"
                  >
                    <X className="size-3" />
                  </button>
                )}
              </span>
            ))
          )}
        </div>
        {canManage && (
          <div className="flex items-center gap-2">
            <TextInput
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addDomain();
                }
              }}
              placeholder="beispiel.ch"
              className="max-w-xs"
            />
            <Button variant="outline" size="sm" onClick={addDomain}>
              <Plus className="size-4" /> Hinzufügen
            </Button>
          </div>
        )}
      </div>

      <div>
        <label
          htmlFor="mail-default-team"
          className="mb-1 block text-xs font-medium text-muted-foreground"
        >
          Nicht zugeordnete Post sichtbar für
        </label>
        <p className="mb-2 text-xs text-muted-foreground">
          Team, das E-Mails sehen darf, die keinem Kunden zugeordnet werden konnten.
        </p>
        <select
          id="mail-default-team"
          aria-label="Nicht zugeordnete Post sichtbar für"
          disabled={!canManageVisibility || saveMailDefaultTeam.isPending}
          value={effectiveMailTeam ?? ''}
          onChange={(e) => {
            const next = e.target.value === '' ? null : e.target.value;
            setPickedMailTeam({ value: next, base: serverMailTeam });
            setMailTeamError(null);
            saveMailDefaultTeam.mutate(next);
          }}
          className="h-9 rounded-md border border-border bg-background px-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
        >
          <option value="">Keine Einschränkung</option>
          {(mailTeams ?? []).map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
          {/* A configured team must stay on screen even unnamed. The api's own name wins when
              it has one - it is the only source a viewer without admin.team.read has - and it
              names only the team the SERVER holds: a pick held optimistically may be a
              different team, whose name comes from the list. */}
          {effectiveMailTeam != null && namedMailTeam == null && (
            <option value={effectiveMailTeam}>
              {effectiveMailTeam === serverMailTeam && serverMailTeamName != null
                ? serverMailTeamName
                : mailTeams != null
                  ? UNKNOWN_MAIL_TEAM_LABEL
                  : kundenteams.isLoading
                    ? LOADING_MAIL_TEAM_LABEL
                    : UNRESOLVED_MAIL_TEAM_LABEL}
            </option>
          )}
        </select>
        {mailTeamError != null && (
          <div role="alert" className="mt-1 text-sm text-status-danger">
            {mailTeamError}
          </div>
        )}
      </div>

      {error != null && <div className="text-sm text-status-danger">{error}</div>}
      {canManage && (
        <div className="flex justify-end">
          <Button disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
            Speichern
          </Button>
        </div>
      )}
    </div>
  );
}

// ── Portal tab ──────────────────────────────────────────────────────────────
function PortalTab() {
  const qc = useQueryClient();
  const { data, isLoading, isError } = usePortalAdminConfig();
  const [form, setForm] = useState<PortalConfigDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const cfg = form ?? data ?? null;

  const save = useMutation({
    mutationFn: () => {
      if (cfg == null) throw new Error('Keine Konfiguration.');
      const toggles = Object.fromEntries(
        PORTAL_FEATURE_KEYS.map((k) => [k, cfg.feature_toggles[k] ?? false]),
      ) as PortalFeatureToggles;
      return apiClient.admin.setPortalConfig({
        enabled: cfg.enabled,
        // Branding is owned by the dedicated "Branding" tab (live self-edit endpoint).
        // This save deliberately OMITS branding — the server preserves the stored blob
        // untouched, so a live branding change made in the other tab is never clobbered
        // (and the W4 colour keys are never round-tripped into a DTO that forbids them).
        feature_toggles: toggles,
        invite_criteria: cfg.invite_criteria,
        reminder_cascade: cfg.reminder_cascade,
        access_retention_days: cfg.access_retention_days,
        activation_link_days: cfg.activation_link_days,
      });
    },
    onSuccess: () => {
      setError(null);
      setForm(null);
      void qc.invalidateQueries({ queryKey: qk.adminPortalConfig() });
    },
    onError: (e: Error) => setError(e.message),
  });

  if (isLoading) return <LoadingState />;
  if (isError || cfg == null) return <ErrorState />;

  const update = (patch: Partial<PortalConfigDto>) => setForm({ ...cfg, ...patch });
  const toggleFeature = (k: string, v: boolean) =>
    update({ feature_toggles: { ...cfg.feature_toggles, [k]: v } });
  const toggleStatus = (s: string) => {
    const cur = cfg.invite_criteria.kundenStatuses;
    const next = cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s];
    update({ invite_criteria: { ...cfg.invite_criteria, kundenStatuses: next } });
  };

  return (
    <div className="space-y-6">
      {cfg.is_default && (
        <div className="rounded-md border border-status-info-border bg-status-info-bg/40 px-3 py-2 text-xs text-status-info">
          Standardwerte, noch nicht gespeichert.
        </div>
      )}

      <div className="divide-y divide-border rounded-xl border border-border bg-card px-4">
        <SettingRow label="Kundenportal aktiv" hint="Portalzugang für Kundinnen und Kunden.">
          <Toggle
            checked={cfg.enabled}
            onChange={(v) => update({ enabled: v })}
            label="Kundenportal aktiv"
          />
        </SettingRow>
      </div>

      <div>
        <div className="mb-2 text-sm font-semibold">Funktionen</div>
        <div className="divide-y divide-border rounded-xl border border-border bg-card px-4">
          {PORTAL_FEATURE_KEYS.map((k) => (
            <SettingRow key={k} label={FEATURE_LABELS[k] ?? k}>
              <Toggle
                checked={cfg.feature_toggles[k] ?? false}
                onChange={(v) => toggleFeature(k, v)}
                label={FEATURE_LABELS[k] ?? k}
              />
            </SettingRow>
          ))}
        </div>
      </div>

      <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
        Logo und Markenfarben werden im Tab „Branding" bearbeitet. Änderungen wirken sofort
        auf Kundenmails und das Portal.
      </div>

      <div>
        <div className="mb-2 text-sm font-semibold">Einladungskriterien</div>
        <p className="mb-2 text-xs text-muted-foreground">
          Nur Kundinnen und Kunden in diesen Status kommen für automatische Einladungen infrage.
        </p>
        <div className="mb-3 flex flex-wrap gap-1.5">
          {CUSTOMER_STATUS_WIRE_VALUES.map((s) => {
            const on = cfg.invite_criteria.kundenStatuses.includes(s);
            return (
              <button
                key={s}
                type="button"
                aria-pressed={on}
                onClick={() => toggleStatus(s)}
                className={
                  on
                    ? 'rounded-md border border-primary bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary'
                    : 'rounded-md border border-border bg-background px-2 py-0.5 text-xs text-muted-foreground hover:text-foreground'
                }
              >
                {STATUS_LABELS[s] ?? s}
              </button>
            );
          })}
        </div>
        <div className="divide-y divide-border rounded-xl border border-border bg-card px-4">
          <SettingRow label="E-Mail erforderlich" hint="Kunden ohne E-Mail werden übersprungen.">
            <Toggle
              checked={cfg.invite_criteria.requireEmail}
              onChange={(v) =>
                update({ invite_criteria: { ...cfg.invite_criteria, requireEmail: v } })
              }
              label="E-Mail erforderlich"
            />
          </SettingRow>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs text-muted-foreground">
            Erinnerungs-Kadenz (Tage, kommagetrennt)
          </label>
          <TextInput
            value={cfg.reminder_cascade.join(', ')}
            onChange={(e) =>
              update({
                reminder_cascade: e.target.value
                  .split(',')
                  .map((n) => Number(n.trim()))
                  .filter((n) => Number.isFinite(n) && n > 0),
              })
            }
            className="tabular-nums"
            placeholder="10, 20, 30"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs text-muted-foreground">
            Zugriffs-Aufbewahrung (Tage)
          </label>
          <TextInput
            inputMode="numeric"
            value={String(cfg.access_retention_days)}
            onChange={(e) => update({ access_retention_days: Number(e.target.value) || 0 })}
            className="tabular-nums"
          />
        </div>
        <div>
          <label htmlFor="portal-activation-link-days" className="mb-1 block text-xs text-muted-foreground">
            Gültigkeit Aktivierungslink (Tage, 7 bis 60)
          </label>
          <TextInput
            id="portal-activation-link-days"
            inputMode="numeric"
            value={String(cfg.activation_link_days ?? 30)}
            onChange={(e) => update({ activation_link_days: Number(e.target.value) || 0 })}
            className="tabular-nums"
          />
        </div>
      </div>

      {error != null && <div className="text-sm text-status-danger">{error}</div>}
      <div className="flex justify-end">
        <Button disabled={form == null || save.isPending} onClick={() => save.mutate()}>
          Speichern
        </Button>
      </div>
    </div>
  );
}

/**
 * LogoThumb — render the stored broker logo as an image, falling back to the broker
 * name wordmark when there is no logo (or the image fails to load). Mirrors the
 * <img src onError> → wordmark pattern from BrandingPreview.
 */
function LogoThumb({ logoUrl, brokerName }: { logoUrl: string | null; brokerName: string }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [logoUrl]);

  return (
    <div className="flex h-20 w-40 items-center justify-center rounded-md border bg-muted/40 p-2">
      {logoUrl && !failed ? (
        <img
          src={logoUrl}
          alt={brokerName}
          onError={() => setFailed(true)}
          className="max-h-full max-w-full object-contain"
        />
      ) : (
        <span className="text-sm font-semibold text-muted-foreground">{brokerName}</span>
      )}
    </div>
  );
}

// ── Branding tab (broker live self-edit — follow-up ①) ──────────────────────
// The broker edits THEIR OWN logo + the three brand colours; the save hits the
// broker-scoped PUT /admin/customer-portal/branding (gated portal_config.manage, NOT
// draft-fenced), so the change is IMMEDIATELY live on customer emails + the portal.
function BrandingTab() {
  const qc = useQueryClient();
  const cfgQ = usePortalAdminConfig();
  const tenantQ = useTenantConfig();
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (patch: Partial<BrokerBrandingDto>) => apiClient.admin.setBrokerBranding(patch),
    onSuccess: () => {
      setError(null);
      setNote('Branding gespeichert, sofort aktiv.');
      void qc.invalidateQueries({ queryKey: qk.adminPortalConfig() });
    },
    onError: (e: Error) => setError(e.message),
  });

  // Upload the bytes → get a logoRef → persist it via the branding self-edit (save).
  const logoUpload = useMutation({
    mutationFn: (file: File) => apiClient.admin.uploadBrokerLogo(file),
    onSuccess: ({ logoRef }) => {
      setError(null);
      save.mutate({ logoRef });
    },
    onError: (e: Error) => setError(e.message),
  });

  if (cfgQ.isLoading || tenantQ.isLoading) return <LoadingState />;
  if (cfgQ.isError || cfgQ.data == null || tenantQ.isError || tenantQ.data == null)
    return <ErrorState />;

  const cfg = cfgQ.data;
  const tenant = tenantQ.data;
  const colors = readBrokerBrandingColors(cfg.branding);
  const hasLogo = typeof cfg.branding.logoRef === 'string' && cfg.branding.logoRef.length > 0;
  const logoUrl = hasLogo ? `${API_BASE_URL}/public/branding/${tenant.id}/logo` : null;
  const logoBusy = logoUpload.isPending || save.isPending;

  return (
    <div className="space-y-6">
      {cfg.is_default && (
        <div className="rounded-md border border-status-info-border bg-status-info-bg/40 px-3 py-2 text-xs text-status-info">
          Standardwerte, noch nicht gespeichert.
        </div>
      )}

      <div>
        <div className="mb-2 text-sm font-semibold">Logo</div>
        <p className="mb-2 text-xs text-muted-foreground">
          Das Broker-Logo für Kundenmails und das Portal.
        </p>
        <div className="flex flex-wrap items-center gap-4">
          <LogoThumb logoUrl={logoUrl} brokerName={tenant.name} />
          <div className="flex flex-col items-start gap-2">
            <label
              className={cn(
                'inline-flex cursor-pointer items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium transition-colors hover:bg-muted',
                logoBusy && 'pointer-events-none opacity-50',
              )}
            >
              <Upload className="size-4" />
              {hasLogo ? 'Logo ersetzen' : 'Logo hochladen'}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                disabled={logoBusy}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = '';
                  if (file) logoUpload.mutate(file);
                }}
              />
            </label>
            {hasLogo && (
              <Button
                variant="ghost"
                size="sm"
                disabled={logoBusy}
                onClick={() => save.mutate({ logoRef: null })}
              >
                Logo entfernen
              </Button>
            )}
          </div>
        </div>
      </div>

      <div>
        <div className="mb-1 text-sm font-semibold">Markenfarben</div>
        <p className="mb-3 text-xs text-muted-foreground">
          Diese drei Farben tragen die kundenseitigen E-Mails und das Kundenportal, in Ihrer
          Marke. Text, Hintergrund und Ränder bleiben zur Lesbarkeit fest.
        </p>
        <BrandingColorsEditor
          key={cfg.version ?? 'default'}
          initialColors={colors}
          brokerName={tenant.name}
          logoUrl={logoUrl}
          saving={save.isPending}
          onSave={(patch: BrandingColorsPatch) => save.mutate(patch)}
          savedNote={note}
        />
      </div>

      {error != null && <div className="text-sm text-status-danger">{error}</div>}
    </div>
  );
}

// ── Portal-Zugänge tab ──────────────────────────────────────────────────────
function PortalZugaengeTab({
  canInvite,
  canRevoke,
  canManageGroups,
}: {
  canInvite: boolean;
  canRevoke: boolean;
  canManageGroups: boolean;
}) {
  const [subjectType, setSubjectType] = useState<'person' | 'firma'>('firma');
  const [subjectId, setSubjectId] = useState('');
  const [lookup, setLookup] = useState<{ type: 'person' | 'firma'; id: string } | null>(null);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end gap-2">
        <div>
          <label className="mb-1 block text-xs text-muted-foreground">Subjekt-Typ</label>
          <select
            aria-label="Subjekt-Typ"
            value={subjectType}
            onChange={(e) => setSubjectType(e.target.value as 'person' | 'firma')}
            className="h-9 rounded-md border border-border bg-background px-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <option value="firma">Firma</option>
            <option value="person">Person</option>
          </select>
        </div>
        <div className="min-w-[16rem] flex-1">
          <label className="mb-1 block text-xs text-muted-foreground">Subjekt-ID</label>
          <TextInput
            value={subjectId}
            onChange={(e) => setSubjectId(e.target.value)}
            placeholder="UUID der Kundin / des Kunden"
            className="font-mono"
          />
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={!subjectId.trim()}
          onClick={() => setLookup({ type: subjectType, id: subjectId.trim() })}
        >
          Portal öffnen
        </Button>
      </div>

      {lookup != null && (
        <PortalBerechtigungenPanel
          key={`${lookup.type}:${lookup.id}`}
          subjectType={lookup.type}
          subjectId={lookup.id}
          canManageGroups={canManageGroups}
          canInvite={canInvite}
          canRevoke={canRevoke}
        />
      )}
    </div>
  );
}

// ── AVB tab ─────────────────────────────────────────────────────────────────
function AvbTab({ canWrite }: { canWrite: boolean }) {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const { data, isLoading, isError } = useAvbLibrary({ q: q.trim() || undefined });
  const [uploadOpen, setUploadOpen] = useState(false);
  const items = data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <div className="max-w-sm flex-1">
          <TextInput
            type="search"
            placeholder="AVB suchen (Titel, Produkt) …"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        {canWrite && (
          <Button variant="outline" size="sm" onClick={() => setUploadOpen((v) => !v)}>
            <Upload className="size-4" /> AVB erfassen
          </Button>
        )}
      </div>

      {uploadOpen && canWrite && <AvbUpload onDone={() => { setUploadOpen(false); void qc.invalidateQueries({ queryKey: qk.adminAvb({ q: q.trim() || undefined }) }); }} />}

      {isLoading ? (
        <LoadingState />
      ) : isError ? (
        <ErrorState />
      ) : items.length === 0 ? (
        <EmptyState label="Keine AVB-Dokumente in der Bibliothek." />
      ) : (
        <ul className="space-y-2">
          {items.map((a) => (
            <li
              key={a.id}
              className="flex items-center justify-between gap-4 rounded-xl border border-border bg-card px-4 py-3"
            >
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold">{a.title}</div>
                <div className="truncate text-xs text-muted-foreground">
                  {a.carrier_name ?? a.carrier_uid} · {a.product_label} · {a.version_label}
                </div>
              </div>
              <span className="shrink-0 font-mono text-[11px] tabular-nums text-muted-foreground">
                {a.valid_from}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function AvbUpload({ onDone }: { onDone: () => void }) {
  const [title, setTitle] = useState('');
  const [carrierUid, setCarrierUid] = useState('');
  const [productLabel, setProductLabel] = useState('');
  const [versionLabel, setVersionLabel] = useState('');
  const [validFrom, setValidFrom] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const submit = useMutation({
    mutationFn: () => {
      if (file == null) throw new Error('Bitte eine PDF-Datei wählen.');
      return apiClient.admin.avbRegister(
        {
          carrier_uid: carrierUid.trim(),
          product_label: productLabel.trim(),
          title: title.trim(),
          version_label: versionLabel.trim(),
          valid_from: validFrom,
        },
        file,
        file.name,
      );
    },
    onSuccess: () => {
      setError(null);
      onDone();
    },
    onError: (e: Error) => setError(e.message),
  });

  const valid =
    title.trim() && carrierUid.trim() && productLabel.trim() && versionLabel.trim() && validFrom && file;

  return (
    <div className="grid gap-3 rounded-xl border border-border bg-card p-4 sm:grid-cols-2">
      <div>
        <label className="mb-1 block text-xs text-muted-foreground">Titel</label>
        <TextInput value={title} onChange={(e) => setTitle(e.target.value)} />
      </div>
      <div>
        <label className="mb-1 block text-xs text-muted-foreground">Versicherer-UID</label>
        <TextInput value={carrierUid} onChange={(e) => setCarrierUid(e.target.value)} placeholder="CHE-…" className="font-mono" />
      </div>
      <div>
        <label className="mb-1 block text-xs text-muted-foreground">Produkt</label>
        <TextInput value={productLabel} onChange={(e) => setProductLabel(e.target.value)} />
      </div>
      <div>
        <label className="mb-1 block text-xs text-muted-foreground">Version</label>
        <TextInput value={versionLabel} onChange={(e) => setVersionLabel(e.target.value)} placeholder="Ausgabe 2026" />
      </div>
      <div>
        <label className="mb-1 block text-xs text-muted-foreground">Gültig ab</label>
        <TextInput type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} />
      </div>
      <div>
        <label className="mb-1 block text-xs text-muted-foreground">Datei (PDF, max. 20 MB)</label>
        <input
          type="file"
          accept="application/pdf"
          aria-label="AVB-Datei"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          className="block w-full text-xs text-muted-foreground file:mr-3 file:rounded-md file:border file:border-border file:bg-background file:px-2 file:py-1 file:text-xs"
        />
      </div>
      {error != null && <div className="text-sm text-status-danger sm:col-span-2">{error}</div>}
      <div className="flex justify-end sm:col-span-2">
        <Button disabled={!valid || submit.isPending} onClick={() => submit.mutate()}>
          Erfassen
        </Button>
      </div>
    </div>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────
export function MandantPage() {
  const { can } = useEffectivePermissions();
  const canTenant = can('tenant.read') || can('tenant.manage');
  const canPortal = can('portal_config.manage');
  const canZugaenge = can('portal_grant.read');
  const canAvb = can('policy.read');
  const tabs = useMemo(() => {
    const list: { key: string; label: string }[] = [];
    if (canTenant) list.push({ key: 'mandant', label: 'Mandant' });
    if (canPortal) list.push({ key: 'portal', label: 'Portal' });
    if (canPortal) list.push({ key: 'branding', label: 'Branding' });
    if (canZugaenge) list.push({ key: 'zugaenge', label: 'Portal-Zugänge' });
    if (canAvb) list.push({ key: 'avb', label: 'AVB' });
    return list;
  }, [canTenant, canPortal, canZugaenge, canAvb]);

  const [active, setActive] = useState<string | null>(null);
  const current = active ?? tabs[0]?.key ?? null;

  return (
    <div>
      <AdminPageHeader title={meta.title} description={meta.description} />
      {current == null ? (
        <EmptyState label="Für diesen Bereich fehlt Ihnen die Berechtigung." />
      ) : (
        <Tabs value={current} onValueChange={setActive}>
          <TabsList variant="underline" className="mb-5">
            {tabs.map((t) => (
              <TabsTrigger key={t.key} value={t.key}>
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="mandant">
            <MandantTab
              canManage={can('tenant.manage')}
              canManageVisibility={can('visibility.manage')}
            />
          </TabsContent>
          <TabsContent value="portal">
            <PortalTab />
          </TabsContent>
          <TabsContent value="branding">
            <BrandingTab />
          </TabsContent>
          <TabsContent value="zugaenge">
            <PortalZugaengeTab
              canInvite={can('portal_access.invite')}
              canRevoke={can('portal_grant.revoke')}
              canManageGroups={can('portal_config.manage')}
            />
          </TabsContent>
          <TabsContent value="avb">
            <AvbTab canWrite={can('policy.write')} />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
